import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { createPortal } from 'react-dom';
import mapboxgl from 'mapbox-gl';
import { getMapboxToken } from '../../lib/config';
import {
  LAYER_CATEGORIES,
  LAYER_DEFINITIONS,
  distanceToFootprint,
  getFeatureCard,
  getLayerFacts,
  resolveTarget,
  type BriefingLayerData,
  type FeatureCard,
  type FeatureTarget,
  type LayerDefinition,
  type LayerFacts,
  type MapEmphasis,
} from './briefing-layer-data';
import './briefing-layer-controls.css';

export interface BriefingLayerControlsHandle {
  selectFeature(feature: GeoJSON.Feature & { layer?: { id: string } }, at: mapboxgl.LngLatLike): boolean;
  closePopup(): void;
  clearLocation(): void;
  resetTransientSelection(): void;
}

interface BriefingLayerControlsProps {
  map: mapboxgl.Map | null;
  data: BriefingLayerData;
  footprint: GeoJSON.FeatureCollection;
  availableMemberIds: string[];
  viewKey: string;
  measurementActive: boolean;
  onEmphasisChange: (emphasis: MapEmphasis | null) => void;
  onBeforeLocation: () => void;
}

interface LocationContext {
  target: FeatureTarget;
  targetPoint: [number, number];
  sitePoint: [number, number];
  name: string;
  distanceLabel: string;
  bounds: mapboxgl.LngLatBounds;
  geojson: GeoJSON.FeatureCollection;
}

interface SavedCamera {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
  padding: mapboxgl.PaddingOptions;
}

const LOCATION_SOURCE_ID = 'briefing-layer-location-context';
const LOCATION_LAYER_IDS = [
  'briefing-layer-location-link',
  'briefing-layer-location-site-fill',
  'briefing-layer-location-site-outline',
] as const;

function targetKey(target: FeatureTarget | null): string {
  if (!target) return '';
  return `${target.layer}|${target.property}|${target.values.join('\u001f')}|${target.point?.join(',') || ''}`;
}

function isPoint(value: unknown): value is [number, number] {
  return Array.isArray(value)
    && value.length >= 2
    && typeof value[0] === 'number'
    && typeof value[1] === 'number'
    && Number.isFinite(value[0])
    && Number.isFinite(value[1]);
}

function extendBounds(bounds: mapboxgl.LngLatBounds, value: unknown): void {
  if (isPoint(value)) {
    bounds.extend([Number(value[0]), Number(value[1])]);
    return;
  }
  if (Array.isArray(value)) value.forEach(item => extendBounds(bounds, item));
}

function pointForTarget(target: FeatureTarget, data: BriefingLayerData): [number, number] | null {
  if (isPoint(target.point)) return [Number(target.point[0]), Number(target.point[1])];
  const pointFeature = resolveTarget(target, data).find(feature => feature.geometry?.type === 'Point');
  const geometry = pointFeature?.geometry;
  const coordinates = geometry?.type === 'Point' ? geometry.coordinates : null;
  return isPoint(coordinates) ? [Number(coordinates[0]), Number(coordinates[1])] : null;
}

function makeLocationContext(
  target: FeatureTarget,
  data: BriefingLayerData,
  footprint: GeoJSON.FeatureCollection,
): LocationContext | null {
  const targetPoint = pointForTarget(target, data);
  if (!targetPoint || footprint.features.length === 0) return null;

  const distance = distanceToFootprint({ type: 'Point', coordinates: targetPoint }, footprint);
  if (distance.miles == null || !isPoint(distance.from) || !isPoint(distance.to)) return null;
  // distanceToFootprint returns [nearest displayed-footprint point, target point].
  // The first keeps the Site marker on the footprint even when the selected
  // layout does not contain the parcel's original stored center.
  const sitePoint: [number, number] = [distance.from[0], distance.from[1]];

  const siteFeatures: GeoJSON.Feature[] = footprint.features.flatMap(feature => {
    if (feature.geometry?.type !== 'Polygon' && feature.geometry?.type !== 'MultiPolygon') return [];
    return [{ type: 'Feature', properties: { kind: 'site' }, geometry: feature.geometry }];
  });
  if (siteFeatures.length === 0) return null;

  const geojson: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [
      ...siteFeatures,
      {
        type: 'Feature',
        properties: { kind: 'link' },
        geometry: { type: 'LineString', coordinates: [distance.from, distance.to] },
      },
    ],
  };
  const bounds = new mapboxgl.LngLatBounds();
  footprint.features.forEach(feature => {
    if (feature.geometry.type !== 'GeometryCollection') extendBounds(bounds, feature.geometry.coordinates);
  });
  bounds.extend(sitePoint);
  bounds.extend(targetPoint);

  return {
    target,
    targetPoint,
    sitePoint,
    name: target.name || 'Selected feature',
    distanceLabel: distance.label,
    bounds,
    geojson,
  };
}

function addLocationLayers(map: mapboxgl.Map, context: LocationContext): void {
  const source = map.getSource(LOCATION_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
  if (source) source.setData(context.geojson);
  else map.addSource(LOCATION_SOURCE_ID, { type: 'geojson', data: context.geojson });

  if (!map.getLayer(LOCATION_LAYER_IDS[0])) {
    map.addLayer({
      id: LOCATION_LAYER_IDS[0],
      type: 'line',
      source: LOCATION_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'link'],
      paint: { 'line-color': '#c5d6df', 'line-width': 1.5, 'line-opacity': 0.85, 'line-dasharray': [2, 2] },
    });
  }
  if (!map.getLayer(LOCATION_LAYER_IDS[1])) {
    map.addLayer({
      id: LOCATION_LAYER_IDS[1],
      type: 'fill',
      source: LOCATION_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'site'],
      paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.16 },
    });
  }
  if (!map.getLayer(LOCATION_LAYER_IDS[2])) {
    map.addLayer({
      id: LOCATION_LAYER_IDS[2],
      type: 'line',
      source: LOCATION_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'site'],
      paint: { 'line-color': '#ffffff', 'line-width': 2, 'line-opacity': 1 },
    });
  }
}

function removeLocationLayers(map: mapboxgl.Map | null): void {
  if (!map) return;
  [...LOCATION_LAYER_IDS].reverse().forEach(id => {
    if (map.getLayer(id)) map.removeLayer(id);
  });
  if (map.getSource(LOCATION_SOURCE_ID)) map.removeSource(LOCATION_SOURCE_ID);
}

function createContextMarker(
  map: mapboxgl.Map,
  point: [number, number],
  label: string,
  kind: 'site' | 'feature',
  onActivate?: () => void,
): mapboxgl.Marker {
  const element = document.createElement('div');
  element.className = `briefing-layer-context-marker briefing-layer-context-${kind}`;
  element.setAttribute('aria-label', kind === 'site' ? 'Subject site' : label);
  element.appendChild(document.createElement('i'));
  const text = document.createElement('span');
  text.textContent = label;
  element.appendChild(text);
  if (onActivate) {
    element.setAttribute('role', 'button');
    element.tabIndex = 0;
    element.style.pointerEvents = 'auto';
    element.style.cursor = 'pointer';
    element.addEventListener('click', event => {
      event.stopPropagation();
      onActivate();
    });
    element.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
      }
    });
  }
  return new mapboxgl.Marker({ element, anchor: 'top', offset: [0, -5] }).setLngLat(point).addTo(map);
}

function Legend({ definition }: { definition: LayerDefinition }) {
  const dashed = definition.id.includes('carrier') || definition.id === 'briefing-railroads';
  const common = { color: definition.color };
  return (
    <svg className="briefing-layer-legend" viewBox="0 0 18 14" aria-hidden="true" style={common}>
      {definition.symbol === 'line' && <path d="M1 7 H17" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={dashed ? '5 3' : undefined} />}
      {definition.symbol === 'hex' && <polygon points="5,1 13,1 17,7 13,13 5,13 1,7" fill="none" stroke="currentColor" strokeWidth="1.4" />}
      {definition.symbol === 'fill' && <rect x="2" y="2" width="14" height="10" fill="currentColor" fillOpacity=".35" stroke="currentColor" />}
      {definition.symbol === 'circle' && <circle cx="9" cy="7" r="4" fill="currentColor" stroke="#e5edf2" strokeWidth="1" />}
    </svg>
  );
}

function FeatureCardContent({ card, definition }: { card: FeatureCard; definition: LayerDefinition | undefined }) {
  return (
    <article className="briefing-layer-feature-card" style={{ '--briefing-feature-color': definition?.color || '#ed9c65' } as React.CSSProperties}>
      <div className="briefing-layer-feature-kind">
        {definition && <Legend definition={definition} />}
        {definition?.label || 'Map feature'}
      </div>
      <h3>{card.title}</h3>
      <p className="briefing-layer-feature-distance">{card.distance}</p>
      {card.details.length > 0 && (
        <ul>{card.details.slice(0, 3).map((detail, index) => <li key={`${detail}-${index}`}>{detail}</li>)}</ul>
      )}
      <footer>{card.source}</footer>
    </article>
  );
}

function LocationInset({ context }: { context: LocationContext }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    setLoadError(false);
    const token = getMapboxToken();
    if (!token) {
      setLoadError(true);
      return;
    }

    const inset = new mapboxgl.Map({
      container,
      accessToken: token,
      style: 'mapbox://styles/mapbox/dark-v11',
      interactive: false,
      attributionControl: false,
      renderWorldCopies: false,
      center: context.sitePoint,
      zoom: 8,
    });
    const markers: mapboxgl.Marker[] = [];
    const onLoad = () => {
      addLocationLayers(inset, context);
      inset.fitBounds(context.bounds, { padding: { top: 30, bottom: 65, left: 32, right: 32 }, maxZoom: 12, duration: 0 });
      markers.push(
        createContextMarker(inset, context.sitePoint, 'Site', 'site'),
        createContextMarker(inset, context.targetPoint, context.name, 'feature'),
      );
    };
    const onError = () => setLoadError(true);
    inset.addControl(new mapboxgl.AttributionControl({ compact: false }), 'bottom-right');
    inset.on('load', onLoad);
    inset.on('error', onError);

    return () => {
      markers.forEach(marker => marker.remove());
      inset.off('load', onLoad);
      inset.off('error', onError);
      inset.remove();
    };
  }, [context]);

  return (
    <figure className="briefing-layer-location-preview">
      <div ref={containerRef} className="briefing-layer-overview-map" aria-label={`Overview of site and ${context.name}`} />
      <figcaption>{context.distanceLabel}</figcaption>
      <small>{loadError ? 'Some overview map data could not load.' : 'Approx. straight-line from displayed site edge'}</small>
    </figure>
  );
}

export const BriefingLayerControls = forwardRef<BriefingLayerControlsHandle, BriefingLayerControlsProps>(function BriefingLayerControls({
  map,
  data,
  footprint,
  availableMemberIds,
  viewKey,
  measurementActive,
  onEmphasisChange,
  onBeforeLocation,
}, ref) {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [previewGroup, setPreviewGroup] = useState<string | null>(null);
  const [openMember, setOpenMember] = useState<string | null>(null);
  const [previewMember, setPreviewMember] = useState<string | null>(null);
  const [previewTarget, setPreviewTarget] = useState<FeatureTarget | null>(null);
  const [selectedCard, setSelectedCard] = useState<FeatureCard | null>(null);
  const [locationTarget, setLocationTarget] = useState<FeatureTarget | null>(null);
  const [mainLocationTarget, setMainLocationTarget] = useState<FeatureTarget | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const popupRootRef = useRef<Root | null>(null);
  const mapRemovedRef = useRef(false);
  const selectedFeatureRef = useRef<(GeoJSON.Feature & { layer?: { id: string } }) | null>(null);
  const controlsRef = useRef<HTMLElement | null>(null);
  const mainMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const savedCameraRef = useRef<SavedCamera | null>(null);
  const previousViewKeyRef = useRef(viewKey);
  const onEmphasisChangeRef = useRef(onEmphasisChange);
  onEmphasisChangeRef.current = onEmphasisChange;

  const availableKey = availableMemberIds.join('\u001f');
  const available = useMemo(() => new Set(availableMemberIds), [availableKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const definitions = useMemo(
    () => new Map(LAYER_DEFINITIONS.map(definition => [definition.id, definition])),
    [],
  );
  const categories = useMemo(
    () => LAYER_CATEGORIES
      .map(category => ({ ...category, memberIds: category.memberIds.filter(memberId => available.has(memberId)) }))
      .filter(category => category.memberIds.length > 0),
    [available],
  );
  const selectedCategory = categories.find(category => category.id === selectedGroup) || null;

  const disposePopupContent = useCallback(() => {
    const root = popupRootRef.current;
    popupRootRef.current = null;
    if (root) queueMicrotask(() => root.unmount());
  }, []);

  const closePopup = useCallback(() => {
    const popup = popupRef.current;
    if (popup) popup.remove();
    else disposePopupContent();
  }, [disposePopupContent]);

  const clearMainContext = useCallback((restoreCamera: boolean) => {
    mainMarkersRef.current.forEach(marker => marker.remove());
    mainMarkersRef.current = [];
    if (!mapRemovedRef.current) removeLocationLayers(map);
    if (restoreCamera && savedCameraRef.current && map) {
      closePopup();
      map.stop();
      map.jumpTo(savedCameraRef.current);
    }
    savedCameraRef.current = null;
    setMainLocationTarget(null);
  }, [closePopup, map]);

  const clearLocation = useCallback(() => {
    setLocationTarget(null);
    clearMainContext(false);
  }, [clearMainContext]);

  const clearSelection = useCallback(() => {
    closePopup();
    setLocationTarget(null);
    setSelectedGroup(null);
    setPreviewGroup(null);
    setOpenMember(null);
    setPreviewMember(null);
    setPreviewTarget(null);
    setSelectedCard(null);
    selectedFeatureRef.current = null;
  }, [closePopup]);

  const resetTransientSelection = useCallback(() => {
    closePopup();
    setLocationTarget(null);
    setPreviewGroup(null);
    setOpenMember(null);
    setPreviewMember(null);
    setPreviewTarget(null);
    setSelectedCard(null);
    selectedFeatureRef.current = null;
    clearMainContext(false);
  }, [clearMainContext, closePopup]);

  const showPopup = useCallback((card: FeatureCard, at: mapboxgl.LngLatLike) => {
    if (!map) return;
    closePopup();
    const definition = definitions.get(card.memberId);
    const content = document.createElement('div');
    const root = createRoot(content);
    root.render(<FeatureCardContent card={card} definition={definition} />);
    popupRootRef.current = root;

    const point = map.project(at);
    const width = map.getContainer().clientWidth;
    const height = map.getContainer().clientHeight;
    const panel = controlsRef.current?.querySelector('.briefing-layer-member-area')?.getBoundingClientRect();
    const mapRect = map.getContainer().getBoundingClientRect();
    const controlsRect = controlsRef.current?.getBoundingClientRect();
    const panelRight = panel
      ? panel.right - mapRect.left
      : controlsRect
        ? Math.min(mapRect.width, controlsRect.left - mapRect.left + 234)
        : 234;
    const side = width < 440 ? null : point.x < panelRight + 125 ? 'left' : point.x > width - 150 ? 'right' : null;
    const anchor = side
      ? `${point.y < 150 ? 'top-' : point.y > height - 180 ? 'bottom-' : ''}${side}` as mapboxgl.Anchor
      : undefined;
    const offset: number | [number, number] = side === 'left'
      ? [Math.max(14, panelRight - point.x + 12), 0]
      : side === 'right' ? [-14, 0] : 14;
    const popup = new mapboxgl.Popup({
      anchor,
      closeButton: true,
      closeOnClick: true,
      focusAfterOpen: false,
      maxWidth: '248px',
      className: 'briefing-layer-feature-popup',
      offset,
    }).setLngLat(at).setDOMContent(content).addTo(map);
    popupRef.current = popup;
    popup.on('close', () => {
      if (popupRef.current === popup) popupRef.current = null;
      if (popupRootRef.current === root) popupRootRef.current = null;
      queueMicrotask(() => root.unmount());
    });
  }, [closePopup, definitions, disposePopupContent, map]);

  const selectFeature = useCallback((
    feature: GeoJSON.Feature & { layer?: { id: string } },
    at: mapboxgl.LngLatLike,
  ): boolean => {
    if (!map || measurementActive) return false;
    const card = getFeatureCard(feature, data, footprint);
    if (!card) return false;
    const category = LAYER_CATEGORIES.find(item => item.memberIds.includes(card.memberId));
    if (!category || !available.has(card.memberId)) return false;

    setSelectedGroup(category.id);
    setPreviewGroup(null);
    setPreviewMember(null);
    setPreviewTarget(null);
    setOpenMember(card.memberId);
    setSelectedCard(card);
    selectedFeatureRef.current = feature;
    setLocationTarget(null);
    showPopup(card, at);
    return true;
  }, [available, data, footprint, map, measurementActive, showPopup]);

  useImperativeHandle(
    ref,
    () => ({ selectFeature, closePopup, clearLocation, resetTransientSelection }),
    [clearLocation, closePopup, resetTransientSelection, selectFeature],
  );

  const emphasis = useMemo<MapEmphasis | null>(() => {
    const fiberSelected = selectedGroup === 'fiber';
    if (previewMember) return { memberIds: [previewMember], target: previewTarget, fiberSelected };
    if (previewGroup) {
      const category = categories.find(item => item.id === previewGroup);
      return category ? { memberIds: category.memberIds, fiberSelected } : null;
    }
    if (selectedCard) return { memberIds: [selectedCard.memberId], target: selectedCard.target, fiberSelected };
    if (selectedCategory) return { memberIds: selectedCategory.memberIds, fiberSelected };
    return null;
  }, [categories, previewGroup, previewMember, previewTarget, selectedCard, selectedCategory, selectedGroup]);

  useEffect(() => {
    onEmphasisChangeRef.current(emphasis);
  }, [emphasis]);

  useEffect(() => {
    if (!measurementActive) return;
    closePopup();
    setPreviewGroup(null);
    setPreviewMember(null);
    setPreviewTarget(null);
  }, [closePopup, measurementActive]);

  useEffect(() => {
    const feature = selectedFeatureRef.current;
    if (!feature) return;
    const refreshed = getFeatureCard(feature, data, footprint);
    if (!refreshed) {
      closePopup();
      setSelectedCard(null);
      selectedFeatureRef.current = null;
      return;
    }
    const popupAt = popupRef.current?.getLngLat();
    setSelectedCard(refreshed);
    if (popupAt) showPopup(refreshed, popupAt);
  }, [closePopup, data, footprint, showPopup]);

  useEffect(() => {
    if (previousViewKeyRef.current === viewKey) return;
    previousViewKeyRef.current = viewKey;
    clearSelection();
    clearMainContext(false);
  }, [clearMainContext, clearSelection, viewKey]);

  useEffect(() => () => onEmphasisChangeRef.current(null), []);

  useEffect(() => {
    if (!map) return;
    mapRemovedRef.current = false;
    const onRemove = () => { mapRemovedRef.current = true; };
    map.on('remove', onRemove);
    map.on('movestart', closePopup);
    map.on('resize', closePopup);
    return () => {
      if (!mapRemovedRef.current) {
        map.off('remove', onRemove);
        map.off('movestart', closePopup);
        map.off('resize', closePopup);
      }
    };
  }, [closePopup, map]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !measurementActive) clearSelection();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [clearSelection, measurementActive]);

  useEffect(() => () => {
    closePopup();
    mainMarkersRef.current.forEach(marker => marker.remove());
    mainMarkersRef.current = [];
    if (!mapRemovedRef.current) removeLocationLayers(map);
  }, [closePopup, map]);

  const locationContext = useMemo(
    () => locationTarget ? makeLocationContext(locationTarget, data, footprint) : null,
    [data, footprint, locationTarget],
  );
  const mainLocationContext = useMemo(
    () => mainLocationTarget ? makeLocationContext(mainLocationTarget, data, footprint) : null,
    [data, footprint, mainLocationTarget],
  );

  useEffect(() => {
    if (!map || !mainLocationContext) return;
    const activateTarget = () => {
      const definition = LAYER_DEFINITIONS.find(item => item.dataKey === mainLocationContext.target.layer);
      const feature = resolveTarget(mainLocationContext.target, data)[0];
      if (!definition || !feature) return;
      selectFeature({ ...feature, layer: { id: definition.id } }, mainLocationContext.targetPoint);
    };
    addLocationLayers(map, mainLocationContext);
    mainMarkersRef.current.forEach(marker => marker.remove());
    mainMarkersRef.current = [
      createContextMarker(map, mainLocationContext.sitePoint, 'Site', 'site'),
      createContextMarker(map, mainLocationContext.targetPoint, mainLocationContext.name, 'feature', activateTarget),
    ];
  }, [data, mainLocationContext, map, selectFeature]);

  const viewOnMainMap = useCallback(() => {
    if (!map || !locationContext) return;
    onBeforeLocation();
    closePopup();
    if (!savedCameraRef.current) {
      savedCameraRef.current = {
        center: map.getCenter().toArray() as [number, number],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
        padding: map.getPadding(),
      };
    }
    addLocationLayers(map, locationContext);
    mainMarkersRef.current.forEach(marker => marker.remove());
    mainMarkersRef.current = [
      createContextMarker(map, locationContext.sitePoint, 'Site', 'site'),
      createContextMarker(map, locationContext.targetPoint, locationContext.name, 'feature'),
    ];
    setMainLocationTarget(locationContext.target);

    const mapRect = map.getContainer().getBoundingClientRect();
    const panel = controlsRef.current?.querySelector('.briefing-layer-member-area')?.getBoundingClientRect();
    const controlsRect = controlsRef.current?.getBoundingClientRect();
    const panelRight = panel?.right
      ?? (controlsRect ? Math.min(mapRect.right, controlsRect.left + 234) : mapRect.left + 234);
    const left = Math.min(panelRight - mapRect.left + 20, mapRect.width * 0.52);
    map.fitBounds(locationContext.bounds, {
      padding: { top: 80, bottom: 90, left, right: 45 },
      maxZoom: 12,
      bearing: 0,
      pitch: 0,
      duration: 450,
      retainPadding: false,
    });
  }, [closePopup, locationContext, map, onBeforeLocation]);

  const openLocation = useCallback((target: FeatureTarget) => {
    onBeforeLocation();
    const same = targetKey(locationTarget) === targetKey(target);
    setLocationTarget(same ? null : target);
  }, [locationTarget, onBeforeLocation]);

  const returnHost = map?.getContainer().querySelector('.mapboxgl-ctrl-top-right');

  return (
    <>
      <section ref={controlsRef} className="briefing-layer-controls" aria-label="Grouped map layers">
        <nav className="briefing-layer-category-list" aria-label="Map layer categories">
          {categories.map(category => (
            <button
              key={category.id}
              type="button"
              aria-pressed={selectedGroup === category.id}
              aria-expanded={selectedGroup === category.id}
              className={previewGroup === category.id ? 'is-previewing' : undefined}
              title={`${category.label} — hover to highlight; click for facts`}
              onMouseEnter={() => setPreviewGroup(category.id)}
              onMouseLeave={() => setPreviewGroup(null)}
              onFocus={() => setPreviewGroup(category.id)}
              onBlur={() => setPreviewGroup(null)}
              onClick={() => {
                const closing = selectedGroup === category.id;
                closePopup();
                setLocationTarget(null);
                setSelectedGroup(closing ? null : category.id);
                setPreviewGroup(null);
                setPreviewMember(null);
                setPreviewTarget(null);
                setOpenMember(null);
                setSelectedCard(null);
                selectedFeatureRef.current = null;
              }}
            >
              {category.label}
            </button>
          ))}
        </nav>

        {!selectedCategory && (
          <div className="briefing-layer-idle-hint">{categories.length ? 'Hover to highlight · Click for facts' : 'Parcel only'}</div>
        )}

        {selectedCategory && (
          <section className="briefing-layer-member-area" aria-label={`${selectedCategory.label} facts`}>
            <header>
              <strong>{selectedCategory.label}</strong>
              <button type="button" onClick={clearSelection} aria-label="Clear selected category">Clear ×</button>
            </header>
            <div className="briefing-layer-facts-body">
              {selectedCategory.memberIds.map(memberId => {
                const definition = definitions.get(memberId);
                if (!definition) return null;
                const picked = selectedCard?.memberId === memberId ? selectedCard : null;
                const isOpen = openMember === memberId;
                const facts: LayerFacts | null = !isOpen ? null : picked
                  ? {
                      items: [
                        { text: `${picked.title} · ${picked.distance}`, target: picked.target },
                        ...picked.details.slice(0, 2).map(text => ({ text, target: picked.target })),
                      ],
                      source: picked.source,
                    }
                  : getLayerFacts(memberId, data, footprint);
                const renderedLocationKeys = new Set<string>();
                return (
                  <section
                    key={memberId}
                    className={`briefing-layer-facts${picked ? ' is-feature-selected' : ''}${previewMember === memberId ? ' is-previewed' : ''}`}
                    style={{ '--briefing-feature-color': definition.color } as React.CSSProperties}
                    onMouseLeave={() => { setPreviewMember(null); setPreviewTarget(null); }}
                    onBlur={event => {
                      if (!event.currentTarget.contains(event.relatedTarget)) {
                        setPreviewMember(null);
                        setPreviewTarget(null);
                      }
                    }}
                  >
                    <button
                      type="button"
                      className="briefing-layer-summary"
                      aria-expanded={isOpen}
                      onClick={() => setOpenMember(isOpen ? null : memberId)}
                      onMouseEnter={() => { setPreviewMember(memberId); setPreviewTarget(null); }}
                      onFocus={() => { setPreviewMember(memberId); setPreviewTarget(null); }}
                    >
                      <span><Legend definition={definition} />{definition.label}</span>
                      <i aria-hidden="true">{isOpen ? '−' : '+'}</i>
                    </button>
                    {isOpen && (
                      <div className="briefing-layer-fact-content">
                        <ul>
                          {facts?.items.map((fact, index) => {
                            const locatable = fact.target ? makeLocationContext(fact.target, data, footprint) : null;
                            const factTargetKey = targetKey(fact.target || null);
                            const showLocation = Boolean(fact.target && locatable && !renderedLocationKeys.has(factTargetKey));
                            if (showLocation) renderedLocationKeys.add(factTargetKey);
                            const locationOpen = fact.target && targetKey(locationTarget) === targetKey(fact.target);
                            return (
                              <li
                                key={`${fact.text}-${index}`}
                                onMouseEnter={() => { setPreviewMember(memberId); setPreviewTarget(fact.target || null); }}
                                onMouseLeave={() => setPreviewTarget(null)}
                              >
                                <span
                                  className={fact.target ? 'briefing-layer-fact-target' : undefined}
                                  tabIndex={fact.target ? 0 : undefined}
                                  onFocus={() => { setPreviewMember(memberId); setPreviewTarget(fact.target || null); }}
                                >
                                  {fact.text}
                                </span>
                                {showLocation && fact.target && (
                                  <button
                                    type="button"
                                    className="briefing-layer-show-location"
                                    aria-expanded={Boolean(locationOpen)}
                                    onClick={() => openLocation(fact.target as FeatureTarget)}
                                  >
                                    {locationOpen ? 'Hide location' : 'Show location'}
                                  </button>
                                )}
                                {showLocation && locationOpen && locationContext && (
                                  <>
                                    <LocationInset context={locationContext} />
                                    <button type="button" className="briefing-layer-overview-action" onClick={viewOnMainMap}>
                                      {mainLocationTarget ? 'Update main map' : 'View on main map'}
                                    </button>
                                  </>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                        <small className="briefing-layer-fact-source">{facts?.source}</small>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </section>
        )}
      </section>

      {mainLocationTarget && returnHost && createPortal(
        <button type="button" className="briefing-layer-return mapboxgl-ctrl" onClick={() => { onBeforeLocation(); clearMainContext(true); }}>
          Return to site
        </button>,
        returnHost,
      )}
    </>
  );
});
